/**
 * Engine ("CAN") signal catalog + resolver. Framework-free (packages/CLAUDE.md rule 1).
 * MIRROR: server/src/lib/can-signals.ts must stay byte-identical from the CanSignalKey type alias
 * onward; src/lib/canSignalsTwin.test.js (root test job) pins it. Behaviour is pinned by
 * server/tests/fixtures/can-signals-contract/cases.json.
 */
export type CanSignalKey =
  | 'rpm' | 'coolantTemp' | 'throttle' | 'engineLoad' | 'obdSpeed' | 'obdOdometer' | 'engineHours'
  | 'fuelLevel' | 'fuelUsed' | 'doorOpen' | 'dtcCount' | 'axleWeight1' | 'axleWeight2' | 'ignitionOnCount';

export interface CanSignalDef { key: CanSignalKey; unit: string; kind: 'number' | 'boolean' | 'counter'; storageScale: number; decimals: number; icon: string }
export interface SourceSpec { source: string; scale?: number; offset?: number; truthy?: number[] }
export type CanOverride = { family?: string } & Partial<Record<CanSignalKey, SourceSpec | null>>;
export interface CanFamily { id: 'teltonika-can' | 'teltonika-obd' | 'generic'; label: string; models: string[]; match: (model: string) => boolean; signals: Partial<Record<CanSignalKey, SourceSpec>> }

export const CAN_SIGNALS: readonly CanSignalDef[] = [
  { key: 'rpm', unit: 'rpm', kind: 'number', storageScale: 1, decimals: 0, icon: 'Gauge' },
  { key: 'coolantTemp', unit: '°C', kind: 'number', storageScale: 1, decimals: 0, icon: 'Thermometer' },
  { key: 'throttle', unit: '%', kind: 'number', storageScale: 1, decimals: 0, icon: 'Activity' },
  { key: 'engineLoad', unit: '%', kind: 'number', storageScale: 1, decimals: 0, icon: 'Activity' },
  { key: 'obdSpeed', unit: 'km/h', kind: 'number', storageScale: 1, decimals: 0, icon: 'Gauge' },
  { key: 'obdOdometer', unit: 'km', kind: 'counter', storageScale: 0.001, decimals: 1, icon: 'Car' },
  { key: 'engineHours', unit: 'h', kind: 'counter', storageScale: 1 / 3_600_000, decimals: 1, icon: 'Clock' },
  { key: 'fuelLevel', unit: '%', kind: 'number', storageScale: 1, decimals: 0, icon: 'Fuel' },
  { key: 'fuelUsed', unit: 'L', kind: 'counter', storageScale: 1, decimals: 1, icon: 'Fuel' },
  { key: 'doorOpen', unit: '', kind: 'boolean', storageScale: 1, decimals: 0, icon: 'DoorOpen' },
  { key: 'dtcCount', unit: '', kind: 'number', storageScale: 1, decimals: 0, icon: 'AlertTriangle' },
  { key: 'axleWeight1', unit: 'kg', kind: 'number', storageScale: 1, decimals: 0, icon: 'Weight' },
  { key: 'axleWeight2', unit: 'kg', kind: 'number', storageScale: 1, decimals: 0, icon: 'Weight' },
  { key: 'ignitionOnCount', unit: '', kind: 'counter', storageScale: 1, decimals: 0, icon: 'Power' },
];

const prefix = (prefixes: string[]) => (model: string) => {
  const head = model.trim().toUpperCase().split(/\s+/)[0] ?? '';
  return prefixes.some((p) => head.startsWith(p));
};

export const CAN_FAMILIES: readonly CanFamily[] = [
  {
    id: 'teltonika-can', label: 'Teltonika CAN (LV-CAN200 / ALL-CAN300)',
    models: ['FMB140', 'FMB120', 'FMB125', 'FMB130', 'FMC130', 'FMB920', 'FMB640', 'FMC640'],
    match: prefix(['FMB1', 'FMB2', 'FMB6', 'FMB9', 'FMC1', 'FMC6', 'FMM1', 'FMU1']),
    signals: {
      rpm: { source: 'rpm' }, coolantTemp: { source: 'engineTemp' }, throttle: { source: 'throttle' }, obdSpeed: { source: 'obdSpeed' },
      obdOdometer: { source: 'obdOdometer' }, engineHours: { source: 'hours' }, fuelLevel: { source: 'fuelLevel' }, fuelUsed: { source: 'fuelUsed' },
      doorOpen: { source: 'io90' }, dtcCount: { source: 'io388' }, axleWeight1: { source: 'io123' }, axleWeight2: { source: 'io124' }, ignitionOnCount: { source: 'io449' },
    },
  },
  {
    id: 'teltonika-obd', label: 'Teltonika OBD (FMB0xx / FMC0xx)',
    models: ['FMB001', 'FMC001', 'FMB003', 'FMM001'],
    match: prefix(['FMB0', 'FMC0', 'FMM0']),
    signals: {
      rpm: { source: 'rpm' }, coolantTemp: { source: 'coolantTemp' }, throttle: { source: 'throttle' }, engineLoad: { source: 'engineLoad' },
      obdSpeed: { source: 'obdSpeed' }, obdOdometer: { source: 'obdOdometer' }, engineHours: { source: 'hours' }, fuelLevel: { source: 'fuelLevel' }, dtcCount: { source: 'dtcs' },
    },
  },
  {
    id: 'generic', label: 'Generic', models: [], match: () => true,
    signals: {
      rpm: { source: 'rpm' }, coolantTemp: { source: 'coolantTemp' }, throttle: { source: 'throttle' }, engineLoad: { source: 'engineLoad' },
      obdSpeed: { source: 'obdSpeed' }, obdOdometer: { source: 'obdOdometer' }, engineHours: { source: 'hours' }, fuelLevel: { source: 'fuelLevel' },
      fuelUsed: { source: 'fuelUsed' }, doorOpen: { source: 'door' }, dtcCount: { source: 'dtcs' }, axleWeight1: { source: 'axleWeight' },
    },
  },
];

export const KNOWN_MODELS: readonly string[] = CAN_FAMILIES.flatMap((f) => f.models);
const SIGNAL_KEYS = new Set<string>(CAN_SIGNALS.map((s) => s.key));
const defOf = (key: CanSignalKey): CanSignalDef => CAN_SIGNALS.find((s) => s.key === key)!;

export function parseCanSignals(raw: unknown): CanOverride {
  let v: unknown = raw;
  if (typeof v === 'string') { try { v = JSON.parse(v); } catch { return {}; } }
  if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
  return v as CanOverride;
}

export function familyFor(model: string | null | undefined, override?: CanOverride): CanFamily {
  const forced = override?.family ? CAN_FAMILIES.find((f) => f.id === override.family) : undefined;
  if (forced) return forced;
  const m = model ?? '';
  return CAN_FAMILIES.find((f) => f.match(m)) ?? CAN_FAMILIES[CAN_FAMILIES.length - 1]!;
}

type DeviceLike = { model?: string | null; attributes?: unknown };
const overrideOf = (device: DeviceLike): CanOverride => {
  const attrs = device.attributes;
  const parsedAttrs = typeof attrs === 'string' ? (() => { try { return JSON.parse(attrs); } catch { return {}; } })() : attrs;
  return parseCanSignals((parsedAttrs as Record<string, unknown> | null)?.canSignals);
};

export function canSignalSource(device: DeviceLike, signal: CanSignalKey) {
  const override = overrideOf(device);
  const family = familyFor(device.model, override);
  const own = override[signal];
  if (own === null) return { source: null, scale: 1, offset: 0, truthy: null, family: family.id, overridden: true, hidden: true };
  const spec = (own && typeof own === 'object' && typeof own.source === 'string') ? own : family.signals[signal];
  if (!spec) return { source: null, scale: 1, offset: 0, truthy: null, family: family.id, overridden: false, hidden: false };
  return {
    source: spec.source, scale: typeof spec.scale === 'number' ? spec.scale : 1, offset: typeof spec.offset === 'number' ? spec.offset : 0,
    truthy: Array.isArray(spec.truthy) ? spec.truthy : null, family: family.id, overridden: spec !== family.signals[signal], hidden: false,
  };
}

export function dtcCountFrom(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw === 'string') return raw.trim() === '' ? 0 : raw.split(',').filter((s) => s.trim() !== '').length;
  return null;
}

export function resolveCanSignal(device: DeviceLike, attributes: Record<string, unknown>, signal: CanSignalKey) {
  const def = defOf(signal);
  const src = canSignalSource(device, signal);
  const base = { unit: def.unit, source: src.source, family: src.family };
  if (src.hidden || !src.source) return { value: null, ...base };
  const raw = attributes?.[src.source];
  if (raw === undefined || raw === null) return { value: null, ...base };
  if (def.kind === 'boolean') {
    if (src.truthy) return { value: src.truthy.includes(Number(raw)), ...base };
    return { value: raw === true || (raw !== false && raw !== 0 && raw !== '0' && raw !== '' && raw !== 'false'), ...base };
  }
  if (signal === 'dtcCount') { const n = dtcCountFrom(raw); return { value: n === null ? null : n * src.scale + src.offset, ...base }; }
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (typeof raw !== 'number' && (typeof raw !== 'string' || raw.trim() === '')) return { value: null, ...base };
  if (!Number.isFinite(n)) return { value: null, ...base };
  const scaled = n * def.storageScale * src.scale + src.offset;
  return { value: Number(scaled.toFixed(6)), ...base };
}

export function resolveCanSignals(device: DeviceLike, attributes: Record<string, unknown>) {
  const out = {} as Record<CanSignalKey, ReturnType<typeof resolveCanSignal>>;
  for (const s of CAN_SIGNALS) out[s.key] = resolveCanSignal(device, attributes, s.key);
  return out;
}

export function isCanSignalKey(k: unknown): k is CanSignalKey { return typeof k === 'string' && SIGNAL_KEYS.has(k); }
